// src/components/admin/modals/InvitationDetailsModal.jsx
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Calendar, Clock, Check, Bell, Send, XCircle } from 'lucide-react';
import axios from '../../../utils/axios';
import { showToast } from '../../../utils/toast';
import { formatDate } from '../../../utils/utils.global';
import RichTextViewer from '../../common/RichTextViewer';

// Same status → color / icon mappings used across both parent components.
export const INVITATION_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  invite_sent: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  accepted: 'bg-green-100 text-green-800 border-green-200',
  review_invitation_accepted: 'bg-green-100 text-green-800 border-green-200',
  review_submitted: 'bg-green-100 text-green-800 border-green-200',
  edit_invitation_accepted: 'bg-green-100 text-green-800 border-green-200',
  edit_submitted: 'bg-green-100 text-green-800 border-green-200',
  declined: 'bg-red-100 text-red-800 border-red-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
  expired: 'bg-gray-100 text-gray-800 border-gray-200',
  completed: 'bg-purple-100 text-purple-800 border-purple-200',
};

export const getStatusColor = (status) =>
  INVITATION_STATUS_COLORS[status] || INVITATION_STATUS_COLORS.pending;

export const getRoleColor = (role) => {
  const colors = {
    editor: 'bg-purple-100 text-purple-800 border-purple-200',
    reviewer: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  return colors[role] || colors.reviewer;
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'pending':
    case 'invite_sent':
      return <Clock className="w-3 h-3 mr-1" />;
    case 'accepted':
    case 'review_invitation_accepted':
    case 'edit_invitation_accepted':
      return <Check className="w-3 h-3 mr-1" />;
    case 'declined':
    case 'cancelled':
      return <XCircle className="w-3 h-3 mr-1" />;
    default:
      return null;
  }
};

// Extract the numeric id from either a raw number or a formatted string id.
const numericId = (invitation) => {
  if (!invitation) return null;
  const id = invitation.id;
  if (typeof id === 'number') return id;
  if (typeof id === 'string') {
    const parts = id.split('-');
    return parts[parts.length - 1];
  }
  return null;
};

/**
 * InvitationDetailsModal
 *
 * Fetches full invitation details (message body, submission info, reminder
 * history) for a given invitation and displays them, plus optional action
 * buttons for reminder / resend / cancel.
 *
 * Props:
 *   invitation       — the base invitation object (from a list)
 *   onClose          — close handler
 *   apiBase          — endpoint prefix (default '/api/semperfi/invitations')
 *   onSendReminder   — optional callback; when provided, shows a "Remind"
 *                      button. Called with the invitation object.
 *   onResend         — optional; called with the invitation
 *   onCancel         — optional; called with the invitation
 *   onViewReminders  — optional; called with the invitation to open reminder history
 */
const InvitationDetailsModal = ({
  invitation,
  onClose,
  apiBase = '/api/semperfi/invitations',
  onSendReminder,
  onResend,
  onCancel,
  onViewReminders,
}) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!invitation) return;

    const load = async () => {
      setLoading(true);
      try {
        const id = numericId(invitation);
        const response = await axios.get(`${apiBase}/${id}`);
        if (!cancelled && response.data.success) {
          setDetails({
            ...invitation,
            ...response.data.invitation,
            sentEmail: response.data.sentEmail || null,
          });
        }
      } catch (error) {
        console.error('Error fetching invitation details:', error);
        if (!cancelled) {
          showToast('Failed to load invitation details', 'error');
          // Fall back to the invitation object we were given
          setDetails(invitation);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [invitation, apiBase]);

  if (!invitation) return null;

  const data = details || invitation;
  const status = data.status || data.invitation_status;
  const role = data.role || (data.invited_for === 'To Edit' ? 'editor' : 'reviewer');

  // Actions are available when the parent provides the handlers.
  const showActions = onSendReminder || onResend || onCancel;

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Invitation Details</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status + role badges */}
              <div className="flex flex-wrap gap-3">
                <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getRoleColor(role)}`}>
                  {role === 'editor' ? 'Editor' : 'Reviewer'}
                </span>
                <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(status)}`}>
                  <StatusIcon status={status} />
                  {status}
                </span>
              </div>

              {/* Title */}
              <div>
                <p className="text-sm font-medium text-[--asfi-purple] mb-1">
                  {data.manuscriptId || data.invitation_link}
                </p>
                <h2 className="text-xl font-bold text-gray-900">{data.title}</h2>
                {data.type && <p className="text-sm text-gray-500 mt-1">{data.type}</p>}
              </div>

              {/* Recipient + invited by */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Recipient</h4>
                  <p className="font-medium text-gray-900">
                    {data.invitedTo || data.invited_user_name || 'Unknown'}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center mt-1">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    {data.invitedToEmail || data.invited_user || 'N/A'}
                  </p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Invited By</h4>
                  <p className="font-medium text-gray-900">
                    {data.invitedBy || 'System'}
                  </p>
                  <p className="text-sm text-gray-600 flex items-center mt-1">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    {data.invitedByEmail || 'system@asfirj.org'}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {(data.invitedDate || data.invitation_date) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Invited Date</h4>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      {data.invitedDate || formatDate(data.invitation_date)}
                    </p>
                  </div>
                )}
                {(data.expiryDate || data.invitation_expiry_date) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Expiry Date</h4>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      {data.expiryDate || formatDate(data.invitation_expiry_date)}
                    </p>
                  </div>
                )}
                {(data.acceptedDate || data.acceptance_date) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-1">Accepted Date</h4>
                    <p className="text-sm text-gray-600 flex items-center">
                      <Check className="w-4 h-4 text-gray-400 mr-2" />
                      {data.acceptedDate || formatDate(data.acceptance_date)}
                    </p>
                  </div>
                )}
              </div>

              {/* Message body */}
              {data.sentEmail?.body && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-medium text-gray-700 flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      Invitation Message
                    </h4>
                    {data.sentEmail.subject && (
                      <span className="text-xs text-gray-500">
                        Subject: {data.sentEmail.subject}
                      </span>
                    )}
                    {data.sentEmail.sent_at && (
                      <span className="text-xs text-gray-500">
                        Sent: {formatDate(data.sentEmail.sent_at)}
                      </span>
                    )}
                  </div>
                  <div className="p-4 max-h-96 overflow-y-auto">
                    <RichTextViewer content={data.sentEmail.body} />
                  </div>
                </div>
              )}

              {/* Submission summary */}
              {data.submission && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Submission Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Discipline:</p>
                      <p className="font-medium text-gray-900">{data.submission.discipline || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Status:</p>
                      <p className="font-medium text-gray-900">{data.submission.submission_status || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Submitted:</p>
                      <p className="font-medium text-gray-900">{data.submission.date_submitted || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Revisions:</p>
                      <p className="font-medium text-gray-900">{data.submission.revisions_count || 0}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Reminder summary */}
              {data.reminderCount > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Reminder Information</h4>
                  <p className="text-sm text-orange-700 mb-2">
                    {data.reminderCount} reminder(s) have been sent
                  </p>
                  {onViewReminders && (
                    <button
                      onClick={() => onViewReminders(data)}
                      className="text-xs text-orange-700 underline hover:text-orange-900"
                    >
                      View reminder details
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex justify-end gap-3 flex-wrap">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>

            {showActions && (
              <>
                {onSendReminder && (
                  <button
                    onClick={() => {
                      onClose();
                      onSendReminder(data);
                    }}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                  >
                    <Bell className="w-4 h-4" />
                    Remind
                  </button>
                )}
                {onResend && (
                  <button
                    onClick={() => {
                      onClose();
                      onResend(data);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Resend
                  </button>
                )}
                {onCancel && (
                  <button
                    onClick={() => {
                      onClose();
                      onCancel(data);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default InvitationDetailsModal;